import React, { useEffect, useState } from 'react';
import { Alert, Backdrop, Box, CircularProgress, Container, Link, Snackbar, Typography } from '@mui/material';
import { Layout } from 'features/common/components/Layout';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { ShowAlertState } from '../../features/common/types';
import { IPassengerDetail } from '../../features/passengers/types';
import { extractErrorMessage, isNotFoundError } from '../../features/quotation/helpers';
import PassengerDetail from '../../features/passengers/components/PassengerDetail';
import { ErrorType, PageInternalError404 } from '../../features/common/components/PageInternalError404';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	loadPassengerWithAgencyContext,
	parseAgencyIdFromSearch,
} from '../../features/passengers/helpers/passengerAgencyContext';
import {
	detailViewModeFromSearch,
	getDetailBackRoute,
	getDetailPageTitle,
} from '../../features/passengers/helpers/detailView';

export const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

export const PassengerDetailPage = (): JSX.Element => {
	const classes = useStyles();
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const viewMode = detailViewModeFromSearch(location.search);
	const dispatch = useDispatch();
	const auth = useSelector((state: RootState) => state.auth);
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [passengerDetail, setPassengerDetail] = useState<IPassengerDetail | null>(null);
	const [showError404, setShowError404] = useState<boolean>(false);

	useEffect(() => {
		const loadPassengerDetails = async () => {
			try {
				setLoading(true);
				setShowError404(false);

				// El cliente puede ser de otra BU: se prueba en las agencies del
				// usuario (la de la URL primero) y se cambia el ambiente solo.
				const { data, switched, countryName } = await loadPassengerWithAgencyContext({
					passengerId: id,
					preferredAgencyId: parseAgencyIdFromSearch(location.search),
					userAgencyIds: auth.user?.agencies.map((agency) => agency.agencyId) ?? [],
					currentCountry: auth.country,
					dispatch,
				});

				setPassengerDetail(data);

				if (switched) {
					setAlert({
						show: true,
						severity: 'info',
						message: `Cambiaste automáticamente a ${countryName} para ver este cliente.`,
					});
				}
			} catch (error) {
				if (isNotFoundError(error)) {
					setShowError404(true);
				} else {
					setAlert({
						show: true,
						severity: 'error',
						message: extractErrorMessage(error, 'Ocurrió un error al recibir los detalles'),
					});
				}
			} finally {
				setLoading(false);
			}
		};

		loadPassengerDetails();
	}, [id]);

	return (
		<>
			<Layout>
				<Container component="main" maxWidth="xl" sx={{ py: 4 }}>
					<Backdrop className={classes.backdrop} open={loading}>
						<CircularProgress color="inherit" />
					</Backdrop>

					<Box sx={{ mb: 3 }}>
						<Link component={RouterLink} to={getDetailBackRoute(viewMode)} underline="hover" variant="body2">
							← Volver a {viewMode === 'client' ? 'clientes' : 'pasajeros'}
						</Link>
						<Typography variant="h5" sx={{ fontWeight: 600, mt: 1 }}>
							{getDetailPageTitle(viewMode)}
						</Typography>
					</Box>

					{passengerDetail && <PassengerDetail passengerDetail={passengerDetail} viewMode={viewMode} />}
					{showError404 && <PageInternalError404 errorType={ErrorType.INTERNAL_AUTHORIZATION_NOT_FUND} />}
				</Container>
			</Layout>
			<Snackbar open={alert.show} autoHideDuration={3000} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
